import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react'
import { Device, Call as TwilioCall, Call } from '@twilio/voice-sdk'

export type CallState = 'idle' | 'connecting' | 'ringing' | 'connected' | 'disconnected'

interface CallStateInfo {
  userName: string
  userId: number
  callSid: string
}

interface ActiveCallInfo {
  userName: string
  userId: number
  callSid: string
  phoneNumber: string
  contactName: string | null
  direction: 'incoming' | 'outgoing'
}

interface ContactInfo {
  id: number
  name: string
  type: 'customer' | 'lead' | 'driver' | 'candidate'
  contact_methods?: Record<string, string>
}

interface TwilioContextValue {
  device: Device | null
  deviceReady: boolean
  deviceError: string | null
  callState: CallState
  activeCall: TwilioCall | null
  isIncoming: boolean
  incomingFrom: string | null
  incomingCallerName: string | null
  activeCallContact: ContactInfo | null
  activeCallDirection: 'incoming' | 'outgoing' | null
  isMuted: boolean
  volume: number
  callDuration: number
  ringtoneMuted: boolean
  answeredBy: CallStateInfo | null
  otherActiveCalls: ActiveCallInfo[]
  makeCall: (phoneNumber: string) => Promise<void>
  answerCall: () => void
  declineCall: () => void
  hangUp: () => void
  toggleMute: () => void
  sendDigits: (digits: string) => void
  setVolume: (volume: number) => void
  toggleRingtoneMute: () => void
}

const TwilioContext = createContext<TwilioContextValue | null>(null)

export function useTwilio(): TwilioContextValue {
  const ctx = useContext(TwilioContext)
  if (!ctx) {
    // Return a no-op context for unauthenticated pages
    return {
      device: null,
      deviceReady: false,
      deviceError: null,
      callState: 'idle',
      activeCall: null,
      isIncoming: false,
      incomingFrom: null,
      incomingCallerName: null,
      activeCallContact: null,
      activeCallDirection: null,
      isMuted: false,
      volume: 1.0,
      callDuration: 0,
      ringtoneMuted: false,
      answeredBy: null,
      otherActiveCalls: [],
      makeCall: async () => {},
      answerCall: () => {},
      declineCall: () => {},
      hangUp: () => {},
      toggleMute: () => {},
      sendDigits: () => {},
      setVolume: () => {},
      toggleRingtoneMute: () => {},
    }
  }
  return ctx
}

interface TwilioProviderProps {
  children: React.ReactNode
  enabled: boolean
  userId: number
  userName: string
  userRole?: string
}

export function TwilioProvider({ children, enabled, userId, userName, userRole }: TwilioProviderProps) {
  const [device, setDevice] = useState<Device | null>(null)
  const [deviceReady, setDeviceReady] = useState(false)
  const [deviceError, setDeviceError] = useState<string | null>(null)
  const [callState, setCallState] = useState<CallState>('idle')
  const [activeCall, setActiveCall] = useState<TwilioCall | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolumeState] = useState(1.0)
  const [callDuration, setCallDuration] = useState(0)
  const [isIncoming, setIsIncoming] = useState(false)
  const [incomingFrom, setIncomingFrom] = useState<string | null>(null)
  const [incomingCallerName, setIncomingCallerName] = useState<string | null>(null)
  const [activeCallContact, setActiveCallContact] = useState<ContactInfo | null>(null)
  const [activeCallDirection, setActiveCallDirection] = useState<'incoming' | 'outgoing' | null>(null)
  const [ringtoneMuted, setRingtoneMuted] = useState(() => {
    try {
      const stored = localStorage.getItem('twilio_ringtone_muted')
      if (stored !== null) return stored === 'true'
      // Default to muted for owners
      return userRole === 'owner'
    } catch { /* localStorage may be unavailable */
      return userRole === 'owner'
    }
  })
  const [answeredBy, setAnsweredBy] = useState<CallStateInfo | null>(null)
  const [otherActiveCalls, setOtherActiveCalls] = useState<ActiveCallInfo[]>([])

  const callTimerRef = useRef<NodeJS.Timeout | null>(null)
  const tokenRefreshRef = useRef<NodeJS.Timeout | null>(null)
  const deviceRef = useRef<Device | null>(null)
  const cableSubscriptionRef = useRef<any>(null)
  const cableRef = useRef<any>(null)
  const activeCallContactRef = useRef<ContactInfo | null>(null)

  // Keep activeCallContactRef in sync with activeCallContact
  useEffect(() => {
    activeCallContactRef.current = activeCallContact
  }, [activeCallContact])

  // ActionCable subscription for call state sharing
  useEffect(() => {
    if (!enabled) return

    // @ts-expect-error - ActionCable is available globally from Rails
    const cable = window.ActionCable?.createConsumer()
    if (!cable) return

    cableRef.current = cable

    const subscription = cable.subscriptions.create(
      { channel: 'CallStateChannel' },
      {
        received(data: any) {
          if (data.user_id === userId) return // Ignore own events

          if (data.type === 'call_answered') {
            setAnsweredBy({
              userName: data.user_name,
              userId: data.user_id,
              callSid: data.call_sid,
            })
          } else if (data.type === 'call_started') {
            // Another user started a call - add to active calls list
            setOtherActiveCalls(prev => {
              // Remove any existing call from this user (in case they didn't properly end previous)
              const filtered = prev.filter(c => c.userId !== data.user_id)
              return [...filtered, {
                userName: data.user_name,
                userId: data.user_id,
                callSid: data.call_sid,
                phoneNumber: data.phone_number,
                contactName: data.contact_name,
                direction: data.direction,
              }]
            })
          } else if (data.type === 'call_ended') {
            // Remove ended call from active calls list
            setOtherActiveCalls(prev => prev.filter(c => c.userId !== data.user_id))
            // Also clear answeredBy if it was this call
            setAnsweredBy(prev => prev?.callSid === data.call_sid ? null : prev)
          }
        },
      }
    )

    cableSubscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      cable.disconnect()
    }
  }, [enabled, userId])

  // Initialize Twilio Device
  useEffect(() => {
    if (!enabled) return

    let mounted = true

    const initializeDevice = async () => {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) return

        const response = await fetch('/phone_calls/token', {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to fetch Twilio token')
        }

        const data = await response.json()

        if (!mounted) return

        const twilioDevice = new Device(data.token, {
          logLevel: 1,
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        })

        deviceRef.current = twilioDevice

        twilioDevice.on('registered', () => {
          if (mounted) {
            setDeviceReady(true)
            setDeviceError(null)
            // Apply initial ringtone mute state to Device
            try {
              const storedMuted = localStorage.getItem('twilio_ringtone_muted')
              const shouldMute = storedMuted !== null ? storedMuted === 'true' : userRole === 'owner'
              twilioDevice.audio?.incoming(!shouldMute)
            } catch {}
          }
        })

        twilioDevice.on('error', (error) => {
          if (mounted) {
            setDeviceError(error.message || 'Device error occurred')
            setDeviceReady(false)
          }
        })

        twilioDevice.on('incoming', (call) => {
          if (mounted) {
            const fromNumber = call.parameters?.From || null
            setActiveCall(call)
            setCallState('ringing')
            setIsIncoming(true)
            setIncomingFrom(fromNumber)
            setIncomingCallerName(null)
            setActiveCallDirection('incoming')
            setActiveCallContact(null)
            setAnsweredBy(null)
            setupCallHandlers(call, fromNumber, 'incoming')

            // Auto-lookup caller contact info
            if (fromNumber) {
              const authToken = localStorage.getItem('auth_token')
              if (authToken) {
                fetch(`/phone_calls/get_contact_info?phone=${encodeURIComponent(fromNumber)}`, {
                  headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                  },
                })
                  .then((r) => r.json())
                  .then((data) => {
                    if (mounted && data.contact) {
                      setIncomingCallerName(data.contact.name)
                      setActiveCallContact(data.contact)
                    }
                  })
                  .catch(() => {})
              }
            }
          }
        })

        await twilioDevice.register()

        if (mounted) {
          setDevice(twilioDevice)

          // Auto-refresh token every 45 minutes to prevent AccessTokenExpired errors
          const TOKEN_REFRESH_MS = 45 * 60 * 1000
          tokenRefreshRef.current = setInterval(async () => {
            try {
              const authToken = localStorage.getItem('auth_token')
              if (!authToken) return

              const refreshResponse = await fetch('/phone_calls/token', {
                headers: {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${authToken}`,
                },
              })

              if (!refreshResponse.ok) return

              const refreshData = await refreshResponse.json()
              twilioDevice.updateToken(refreshData.token)
            } catch {
              // Silent fail — next interval will retry
            }
          }, TOKEN_REFRESH_MS)
        }
      } catch (error) {
        if (mounted) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to initialize device'
          setDeviceError(errorMessage)
        }
      }
    }

    initializeDevice()

    return () => {
      mounted = false
      if (deviceRef.current) {
        const deviceState = deviceRef.current.state
        if (deviceState === Device.State.Registered) {
          deviceRef.current.unregister().catch(() => {})
        }
        deviceRef.current.destroy()
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current)
      }
      if (tokenRefreshRef.current) {
        clearInterval(tokenRefreshRef.current)
      }
    }
  }, [enabled])

  // Mute/unmute Twilio Device's built-in incoming ringtone
  useEffect(() => {
    const dev = deviceRef.current
    if (!dev) return
    try {
      dev.audio?.incoming(ringtoneMuted ? false : true)
    } catch {
      // audio API may not be available on all devices
    }
  }, [ringtoneMuted, deviceReady])

  const setupCallHandlers = useCallback((call: TwilioCall, phoneNumber: string | null, direction: 'incoming' | 'outgoing') => {
    call.on('accept', () => {
      setCallState('connected')
      startCallTimer()

      // Broadcast that this user started a call (use ref to get latest contact info)
      const contactInfo = activeCallContactRef.current
      cableSubscriptionRef.current?.perform('call_started', {
        call_sid: call.parameters?.CallSid,
        user_name: userName,
        user_id: userId,
        phone_number: phoneNumber,
        contact_name: contactInfo?.name || phoneNumber,
        direction: direction,
      })
    })

    const resetCallState = () => {
      setCallState('idle')
      setActiveCall(null)
      setCallDuration(0)
      setIsMuted(false)
      setIsIncoming(false)
      setIncomingFrom(null)
      setIncomingCallerName(null)
      setActiveCallContact(null)
      setActiveCallDirection(null)
      setAnsweredBy(null)
    }

    call.on('disconnect', () => {
      setCallState('disconnected')
      stopCallTimer()
      // Broadcast call ended via ActionCable
      cableSubscriptionRef.current?.perform('call_ended', {
        call_sid: call.parameters?.CallSid,
        user_id: userId,
      })
      setTimeout(resetCallState, 2000)
    })

    call.on('cancel', () => {
      setCallState('disconnected')
      stopCallTimer()
      setTimeout(resetCallState, 2000)
    })

    call.on('reject', () => {
      setCallState('disconnected')
      stopCallTimer()
      setTimeout(resetCallState, 2000)
    })

    call.on('error', (error) => {
      setDeviceError(error.message || 'Call error occurred')
      setCallState('disconnected')
      stopCallTimer()
      setTimeout(resetCallState, 2000)
    })

    call.on('ringing', () => {
      setCallState('ringing')
    })
  }, [userId, userName, incomingCallerName])

  const startCallTimer = () => {
    setCallDuration(0)
    callTimerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1)
    }, 1000)
  }

  const stopCallTimer = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current)
      callTimerRef.current = null
    }
  }

  const logBrowserCall = useCallback((callSid: string, toNumber: string, status: string) => {
    const token = localStorage.getItem('auth_token')
    fetch('/phone_calls/log_browser_call', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({ call_sid: callSid, to_number: toNumber, status }),
    }).catch(() => {})
  }, [])

  const makeCall = async (phoneNumber: string) => {
    if (!device || !deviceReady) {
      setDeviceError('Device not ready. Please wait or refresh the page.')
      return
    }

    if (activeCall) {
      setDeviceError('A call is already in progress')
      return
    }

    try {
      setCallState('connecting')
      setDeviceError(null)
      setActiveCallDirection('outgoing')
      setIncomingFrom(phoneNumber)

      // Fetch contact info before making the call
      const authToken = localStorage.getItem('auth_token')
      if (authToken) {
        fetch(`/phone_calls/get_contact_info?phone=${encodeURIComponent(phoneNumber)}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
        })
          .then((r) => r.json())
          .then((data) => {
            if (data.contact) {
              setIncomingCallerName(data.contact.name)
              setActiveCallContact(data.contact)
            }
          })
          .catch(() => {})
      }

      const call = await device.connect({
        params: {
          To: phoneNumber,
        },
      })

      setActiveCall(call)
      setupCallHandlers(call, phoneNumber, 'outgoing')

      // Log the outbound call to the server for tracking
      const callSid = call.parameters?.CallSid
      if (callSid) {
        logBrowserCall(callSid, phoneNumber, 'initiated')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to make call'
      setDeviceError(errorMessage)
      setCallState('idle')
    }
  }

  const answerCall = () => {
    if (activeCall && isIncoming) {
      activeCall.accept()
      // Notify backend who answered - use parentCallSid for inbound calls (child call has different SID than DB record)
      const parentCallSid = (activeCall as any).customParameters?.get?.('parentCallSid')
      const callSid = parentCallSid || activeCall.parameters?.CallSid
      if (callSid) {
        const token = localStorage.getItem('auth_token')
        fetch('/phone_calls/answered', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
          body: JSON.stringify({ call_sid: callSid }),
        }).catch(() => {})

        // Broadcast via ActionCable that this user answered
        cableSubscriptionRef.current?.perform('call_answered', {
          call_sid: callSid,
          user_name: userName,
          user_id: userId,
        })
      }
    }
  }

  const declineCall = () => {
    if (activeCall && isIncoming) {
      activeCall.reject()
    }
  }

  const hangUp = () => {
    if (activeCall) {
      activeCall.disconnect()
    }
  }

  const toggleMute = () => {
    if (activeCall) {
      const newMutedState = !isMuted
      activeCall.mute(newMutedState)
      setIsMuted(newMutedState)
    }
  }

  const sendDigits = (digits: string) => {
    if (activeCall) {
      activeCall.sendDigits(digits)
    }
  }

  const setVolume = (newVolume: number) => {
    setVolumeState(newVolume)
    if (device) {
      device.audio?.speakerDevices.set('default')
    }
  }

  const toggleRingtoneMute = () => {
    setRingtoneMuted((prev) => {
      const next = !prev
      try {
        localStorage.setItem('twilio_ringtone_muted', String(next))
      } catch { /* localStorage may be unavailable */ }
      return next
    })
  }

  const value: TwilioContextValue = {
    device,
    deviceReady,
    deviceError,
    callState,
    activeCall,
    isIncoming,
    incomingFrom,
    incomingCallerName,
    activeCallContact,
    activeCallDirection,
    isMuted,
    volume,
    callDuration,
    ringtoneMuted,
    answeredBy,
    otherActiveCalls,
    makeCall,
    answerCall,
    declineCall,
    hangUp,
    toggleMute,
    sendDigits,
    setVolume,
    toggleRingtoneMute,
  }

  return (
    <TwilioContext.Provider value={value}>
      {children}
    </TwilioContext.Provider>
  )
}
